import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Upload, FileText, CheckCircle, XCircle, AlertTriangle, ArrowLeft, Table, ArrowRight, Loader } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { usePreviewExcelMutation, useImportExcelMutation, useGetHistoriqueImportsQuery } from '../../services/etudiantsApi';
import { useGetAnneesAcademiquesQuery } from '../../services/anneeAcademiqueApi';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Spinner } from '../../components/ui/Spinner';
import { Badge } from '../../components/ui/Badge';
import { PageHeader } from '../../components/ui/PageHeader';

interface ColumnMapping {
  [fieldName: string]: number;
}

export const ImportExcel: React.FC = () => {
  const navigate = useNavigate();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewData, setPreviewData] = useState<any>(null);
  const [mapping, setMapping] = useState<ColumnMapping>({});
  const [anneeAcademique, setAnneeAcademique] = useState('2025-2026');
  const [importResult, setImportResult] = useState<any>(null);
  const [currentStep, setCurrentStep] = useState<'upload' | 'mapping' | 'import' | 'result'>('upload');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [importProgress, setImportProgress] = useState(0);
  const [importPhase, setImportPhase] = useState<string>('');
  const progressIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Années académiques disponibles : on charge depuis la BD (table
  // AnneeAcademique) pour refléter exactement les années réellement créées
  // côté admin, y compris les historiques (2017-2018, 2020-2021, etc.).
  // Fallback sur une fenêtre calculée si l'API échoue.
  const { data: anneesBD } = useGetAnneesAcademiquesQuery();
  const anneesAcademiques = React.useMemo(() => {
    if (anneesBD && anneesBD.length > 0) {
      // Trier décroissant : année la plus récente en premier
      return [...anneesBD]
        .map((a: any) => a.libelle)
        .sort((a: string, b: string) => b.localeCompare(a));
    }
    const currentYear = new Date().getFullYear();
    const arr: string[] = [];
    for (let i = 2; i >= -10; i--) {
      const year = currentYear + i;
      arr.push(`${year}-${year + 1}`);
    }
    return arr;
  }, [anneesBD]);

  const [previewExcel, { isLoading: previewLoading }] = usePreviewExcelMutation();
  const [importExcel, { isLoading: importLoading }] = useImportExcelMutation();
  const { data: historiqueImports } = useGetHistoriqueImportsQuery({ limit: 10 });

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setPreviewData(null);
    setMapping({});
    setImportResult(null);
    setErrorMessage(null);
    setCurrentStep('upload');
    // Lecture immédiate en mémoire : évite ERR_UPLOAD_FILE_CHANGED si Excel
    // garde le fichier ouvert ou si OneDrive/Drive le re-sync entre la
    // sélection et l'upload. On garde le File-like (nom + type préservés).
    try {
      const buf = await file.arrayBuffer();
      const stableFile = new File([buf], file.name, {
        type: file.type || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        lastModified: file.lastModified,
      });
      setSelectedFile(stableFile);
    } catch (e) {
      // Fallback : si la lecture mémoire échoue, on tente quand même
      // l'ancien comportement.
      setSelectedFile(file);
      setErrorMessage(
        "Impossible de lire le fichier en mémoire. "
        + "Si vous voyez 'ERR_UPLOAD_FILE_CHANGED', fermez Excel sur ce fichier puis réessayez.",
      );
    }
  };

  const handlePreview = async () => {
    if (!selectedFile) return;
    setErrorMessage(null);

    const formData = new FormData();
    formData.append('file', selectedFile);

    try {
      const result = await previewExcel(formData).unwrap();
      setPreviewData(result);
      // Convert suggested mapping from {index: field} to {field: index}
      const invertedMapping: ColumnMapping = {};
      Object.entries(result.mapping_suggere || {}).forEach(([index, field]) => {
        invertedMapping[field as string] = parseInt(index);
      });
      setMapping(invertedMapping);
      setCurrentStep('mapping');
    } catch (error: any) {
      console.error('Erreur lors de la prévisualisation:', error);
      const msg = error?.data?.error || error?.data?.detail || error?.message || 'Erreur lors de la prévisualisation du fichier';
      setErrorMessage(msg);
    }
  };

  const handleMappingChange = (columnIndex: number, fieldName: string) => {
    setMapping(prev => {
      const newMapping = { ...prev };
      if (fieldName === '') {
        // Remove the mapping for this field
        Object.keys(newMapping).forEach(key => {
          if (newMapping[key] === columnIndex) {
            delete newMapping[key];
          }
        });
      } else {
        // Remove any existing mapping for this field
        Object.keys(newMapping).forEach(key => {
          if (newMapping[key] === columnIndex) {
            delete newMapping[key];
          }
        });
        // Add the new mapping
        newMapping[fieldName] = columnIndex;
      }
      return newMapping;
    });
  };

  // Cleanup progress interval on unmount
  useEffect(() => {
    return () => {
      if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
    };
  }, []);

  const startProgressSimulation = useCallback((totalRows: number) => {
    setImportProgress(0);
    setImportPhase('Envoi du fichier au serveur...');

    // Estimate: ~50ms per row for processing
    const estimatedMs = Math.max(2000, totalRows * 50);
    const interval = 200; // update every 200ms
    const steps = estimatedMs / interval;
    const incrementPerStep = 85 / steps; // max 85% before completion

    let currentProgress = 0;
    const phases = [
      { at: 5, text: 'Lecture du fichier Excel...' },
      { at: 15, text: 'Validation des données...' },
      { at: 25, text: `Traitement de ${totalRows} lignes...` },
      { at: 50, text: `Import en cours... (${Math.floor(totalRows / 2)}/${totalRows})` },
      { at: 70, text: `Finalisation... (${Math.floor(totalRows * 0.7)}/${totalRows})` },
      { at: 80, text: 'Enregistrement dans la base de données...' },
    ];

    progressIntervalRef.current = setInterval(() => {
      currentProgress = Math.min(85, currentProgress + incrementPerStep + Math.random() * 0.5);
      setImportProgress(Math.round(currentProgress));

      // Update phase text
      const matchingPhase = [...phases].reverse().find(p => currentProgress >= p.at);
      if (matchingPhase) {
        setImportPhase(matchingPhase.text);
      }
    }, interval);
  }, []);

  const stopProgressSimulation = useCallback((success: boolean) => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
    setImportPhase(success ? 'Import terminé !' : 'Erreur lors de l\'import');
    setImportProgress(100);
  }, []);

  const handleImport = async () => {
    if (!selectedFile) return;
    setErrorMessage(null);

    const totalRows = previewData?.total_rows || 100;
    startProgressSimulation(totalRows);

    try {
      const result = await importExcel({
        file: selectedFile,
        mapping,
        annee_academique: anneeAcademique
      }).unwrap();

      stopProgressSimulation(true);
      // Small delay to show 100% before switching to result
      setTimeout(() => {
        setImportResult(result);
        setCurrentStep('result');
      }, 600);
    } catch (error: any) {
      stopProgressSimulation(false);
      console.error('Erreur lors de l\'import:', error);
      const msg = error?.data?.error || error?.data?.detail || error?.message || 'Erreur lors de l\'import';
      setErrorMessage(msg);
    }
  };

  const resetImport = () => {
    setSelectedFile(null);
    setPreviewData(null);
    setMapping({});
    setImportResult(null);
    setCurrentStep('upload');
    setImportProgress(0);
    setImportPhase('');
    setErrorMessage(null);
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const getFieldLabel = (fieldName: string) => {
    const labels: Record<string, string> = {
      matricule: 'Matricule',
      nom: 'Nom',
      prenoms: 'Prénoms',
      sexe: 'Sexe',
      date_naissance: 'Date de naissance',
      lieu_naissance: 'Lieu de naissance',
      niveau: 'Niveau',
      classe: 'Classe (CS/DD auto-détecté)',
      parcours: 'Parcours',
      option: 'Option / Filière',
      pole: 'Pôle',
      telephone: 'Téléphone',
      email: 'Email',
      actif: 'Actif',
      type_formation: 'Type de formation (FI/CS/DD)'
    };
    return labels[fieldName] || fieldName;
  };

  return (
    <div className="space-y-6">
      {/* Header (unifié — refonte vague 3 lot 1) */}
      <PageHeader
        title="Import Étudiants Excel"
        description="Importez des étudiants depuis un fichier Excel"
        back={
          <Button variant="secondary" size="sm" icon={<ArrowLeft size={14} />}
            onClick={() => navigate('/etudiants')}>
            Retour
          </Button>
        }
        actions={
          <Button variant="secondary" size="sm" onClick={resetImport}>
            Nouveau Import
          </Button>
        }
      />

      {/* Steps Indicator */}
      <div className="flex items-center space-x-4">
        {['upload', 'mapping', 'import', 'result'].map((step, index) => (
          <React.Fragment key={step}>
            <div className={`flex items-center space-x-2 ${
              currentStep === step ? 'text-blue-600' :
              ['upload', 'mapping', 'import', 'result'].indexOf(currentStep) > index ? 'text-green-600' : 'text-gray-400'
            }`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                currentStep === step ? 'bg-blue-100 dark:bg-blue-900/30 border-2 border-blue-600' :
                ['upload', 'mapping', 'import', 'result'].indexOf(currentStep) > index ? 'bg-green-100 dark:bg-green-900/30 border-2 border-green-600' : 'bg-gray-100 dark:bg-gray-700 border-2 border-gray-300 dark:border-gray-600'
              }`}>
                {['upload', 'mapping', 'import', 'result'].indexOf(currentStep) > index ? (
                  <CheckCircle size={16} />
                ) : (
                  index + 1
                )}
              </div>
              <span className="text-sm font-medium capitalize">{step === 'upload' ? 'Téléchargement' : step === 'mapping' ? 'Mapping' : step === 'import' ? 'Import' : 'Résultat'}</span>
            </div>
            {index < 3 && <div className="w-12 h-px bg-gray-300 dark:bg-gray-600"></div>}
          </React.Fragment>
        ))}
      </div>

      {/* Upload Step */}
      {currentStep === 'upload' && (
        <Card>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Fichier Excel
              </label>
              <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                {selectedFile ? (
                  <div className="space-y-2">
                    <FileText size={48} className="mx-auto text-blue-500" />
                    <p className="text-sm text-gray-600 dark:text-gray-400">{selectedFile.name}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{(selectedFile.size / 1024).toFixed(1)} KB</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Upload size={48} className="mx-auto text-gray-400" />
                    <p className="text-sm text-gray-600 dark:text-gray-400">Cliquez pour sélectionner un fichier Excel</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Formats acceptés: .xlsx, .xls</p>
                  </div>
                )}
                <Button
                  variant="secondary"
                  onClick={() => fileInputRef.current?.click()}
                  className="mt-4"
                >
                  {selectedFile ? 'Changer de fichier' : 'Sélectionner un fichier'}
                </Button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Année Académique
              </label>
              <select
                value={anneeAcademique}
                onChange={(e) => setAnneeAcademique(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {anneesAcademiques.map((annee) => (
                  <option key={annee} value={annee}>
                    {annee}
                  </option>
                ))}
              </select>
            </div>

            {errorMessage && (
              <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg">
                <div className="flex items-start gap-2">
                  <XCircle size={16} className="text-red-600 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-red-700 dark:text-red-300">{errorMessage}</p>
                </div>
              </div>
            )}

            <Button
              onClick={handlePreview}
              disabled={!selectedFile || previewLoading}
              className="w-full"
            >
              {previewLoading ? <Spinner size="sm" /> : null}
              Prévisualiser et mapper les colonnes
            </Button>
          </div>
        </Card>
      )}

      {/* Mapping Step */}
      {currentStep === 'mapping' && previewData && (
        <Card>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 flex items-center gap-2">
                  <Table size={20} className="text-blue-600" />
                  Mapping des colonnes
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  {previewData.total_rows} lignes dans le fichier &mdash; Associez chaque colonne au champ correspondant
                </p>
              </div>
              <div className="flex items-center gap-2">
                {previewData.champs_obligatoires.map((field: string) => (
                  <span key={field} className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${
                    Object.keys(mapping).includes(field) ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                  }`}>
                    {Object.keys(mapping).includes(field) ? <CheckCircle size={12} /> : <XCircle size={12} />}
                    {getFieldLabel(field)}
                  </span>
                ))}
              </div>
            </div>

            <div className="overflow-x-auto border dark:border-gray-700 rounded-lg">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
                      Colonne Excel
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
                      Aperçu des données
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase w-64">
                      Champ de la base
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-100 dark:divide-gray-700">
                  {previewData.headers.map((header: string, index: number) => {
                    const mappedField = Object.keys(mapping).find(field => mapping[field] === index) || '';
                    const isRequired = previewData.champs_obligatoires.includes(mappedField);
                    return (
                      <tr key={index} className={mappedField ? (isRequired ? 'bg-green-50/50 dark:bg-green-900/10' : 'bg-blue-50/30 dark:bg-blue-900/10') : ''}>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-gray-100">
                          <span className="bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded text-xs mr-2 text-gray-500 dark:text-gray-400">Col {index + 1}</span>
                          {header}
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400 max-w-xs truncate">
                          {previewData.preview_rows.slice(0, 3).map((row: any, ri: number) => {
                            const val = row?.data ? row.data[index] : row[index];
                            return val != null ? `${val}` : '';
                          }).filter(Boolean).join(' | ')}
                        </td>
                        <td className="px-4 py-3">
                          <select
                            value={mappedField}
                            onChange={(e) => handleMappingChange(index, e.target.value)}
                            className={`block w-full px-3 py-1.5 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 ${
                              mappedField ? 'border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-900/20 text-gray-900 dark:text-gray-100' : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100'
                            }`}
                          >
                            <option value="">-- Ignorer --</option>
                            <optgroup label="Obligatoires">
                              {previewData.champs_obligatoires.map((field: string) => (
                                <option key={field} value={field}>{getFieldLabel(field)} *</option>
                              ))}
                            </optgroup>
                            <optgroup label="Optionnels">
                              {previewData.champs_optionnels.map((field: string) => (
                                <option key={field} value={field}>{getFieldLabel(field)}</option>
                              ))}
                            </optgroup>
                          </select>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {errorMessage && (
              <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg">
                <div className="flex items-start gap-2">
                  <XCircle size={16} className="text-red-600 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-red-700 dark:text-red-300">{errorMessage}</p>
                </div>
              </div>
            )}

            <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
              <Button variant="outline" icon={<ArrowLeft size={16} />} onClick={() => setCurrentStep('upload')}>
                Retour
              </Button>
              <Button
                icon={<ArrowRight size={16} />}
                onClick={() => setCurrentStep('import')}
                disabled={previewData.champs_obligatoires.some((field: string) => !Object.keys(mapping).includes(field))}
              >
                Continuer vers l'import
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Import Step */}
      {currentStep === 'import' && (
        <Card>
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Confirmer l'import</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {previewData.total_rows} lignes détectées dans le fichier
              </p>
            </div>

            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
              <div className="flex">
                <AlertTriangle className="h-5 w-5 text-blue-400" />
                <div className="ml-3">
                  <h4 className="text-sm font-medium text-blue-800 dark:text-blue-200">
                    Vérification du mapping
                  </h4>
                  <div className="mt-2 text-sm text-blue-700 dark:text-blue-300">
                    <ul className="list-disc pl-5 space-y-1">
                      {previewData.champs_obligatoires.map((field: string) => (
                        <li key={field}>
                          {getFieldLabel(field)}: {
                            Object.keys(mapping).includes(field) ? (
                              <span className="text-green-600">✓ Mappé</span>
                            ) : (
                              <span className="text-red-600">✗ Manquant</span>
                            )
                          }
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Progress Bar */}
            {importLoading && (
              <div className="space-y-3 py-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Loader size={16} className="animate-spin text-blue-500" />
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {importPhase}
                    </span>
                  </div>
                  <span className="text-sm font-bold text-blue-600 dark:text-blue-400 tabular-nums">
                    {importProgress}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-300 ease-out bg-gradient-to-r from-blue-500 to-blue-600"
                    style={{ width: `${importProgress}%` }}
                  />
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                  Traitement de {previewData.total_rows} lignes — Ne fermez pas cette page
                </p>
              </div>
            )}

            {errorMessage && (
              <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg">
                <div className="flex items-start gap-2">
                  <XCircle size={16} className="text-red-600 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-red-700 dark:text-red-300">{errorMessage}</p>
                </div>
              </div>
            )}

            <div className="flex space-x-4">
              <Button variant="secondary" onClick={() => setCurrentStep('mapping')} disabled={importLoading}>
                Retour au mapping des colonnes
              </Button>
              <Button
                onClick={handleImport}
                disabled={importLoading || previewData.champs_obligatoires.some((field: string) => !Object.keys(mapping).includes(field))}
                className="flex-1"
              >
                {importLoading ? (
                  <span className="flex items-center gap-2">
                    <Loader size={16} className="animate-spin" />
                    Import en cours...
                  </span>
                ) : (
                  'Lancer l\'import'
                )}
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Result Step */}
      {currentStep === 'result' && importResult && (
        <Card>
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              {importResult.success ? (
                <CheckCircle className="h-6 w-6 text-green-500" />
              ) : (
                <XCircle className="h-6 w-6 text-red-500" />
              )}
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                {importResult.success ? 'Import réussi' : 'Import terminé avec des erreurs'}
              </h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{importResult.rapport.nb_total_lignes}</div>
                <div className="text-sm text-blue-600 dark:text-blue-400">Lignes traitées</div>
              </div>
              <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                <div className="text-2xl font-bold text-green-600 dark:text-green-400">{importResult.rapport.nb_etudiants_crees}</div>
                <div className="text-sm text-green-600 dark:text-green-400">Étudiants créés</div>
              </div>
              <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg">
                <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">{importResult.rapport.nb_etudiants_maj}</div>
                <div className="text-sm text-yellow-600 dark:text-yellow-400">Étudiants mis à jour</div>
              </div>
              <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg">
                <div className="text-2xl font-bold text-red-600 dark:text-red-400">{importResult.rapport.nb_erreurs}</div>
                <div className="text-sm text-red-600 dark:text-red-400">Erreurs</div>
              </div>
            </div>

            {importResult.rapport.erreurs.length > 0 && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-4">
                <h4 className="text-sm font-medium text-red-800 dark:text-red-300 mb-2">Erreurs rencontrées:</h4>
                <ul className="text-sm text-red-700 dark:text-red-400 space-y-1 max-h-40 overflow-y-auto">
                  {importResult.rapport.erreurs.map((error: any, index: number) => (
                    <li key={index}>
                      Ligne {error.row}: {error.error}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {importResult.rapport.warnings.length > 0 && (
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg p-4">
                <h4 className="text-sm font-medium text-yellow-800 dark:text-yellow-300 mb-2">Avertissements:</h4>
                <ul className="text-sm text-yellow-700 dark:text-yellow-400 space-y-1 max-h-40 overflow-y-auto">
                  {importResult.rapport.warnings.map((warning: any, index: number) => (
                    <li key={index}>
                      {warning.message}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex gap-3">
              <Button onClick={resetImport} variant="outline" className="flex-1">
                Nouvel import
              </Button>
              <Button onClick={() => navigate('/etudiants')} variant="primary" className="flex-1">
                Voir les étudiants
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Historique des imports */}
      {historiqueImports && historiqueImports.length > 0 && (
        <Card>
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Historique des imports récents</h3>
            <div className="space-y-2">
              {historiqueImports.map((import_: any) => (
                <div key={import_.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{import_.fichier_nom}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {new Date(import_.date_import).toLocaleString('fr-FR')}
                    </p>
                  </div>
                  <div className="flex space-x-2">
                    <Badge variant={import_.nb_erreurs > 0 ? 'danger' : 'success'}>
                      {import_.nb_etudiants_crees + import_.nb_etudiants_maj} étudiants
                    </Badge>
                    {import_.nb_erreurs > 0 && (
                      <Badge variant="warning">
                        {import_.nb_erreurs} erreurs
                      </Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};